import { lazy, Suspense, useEffect, useState, type JSX } from 'react';
import {
  Navigate,
  createBrowserRouter,
  useLocation,
  useSearchParams,
  type RouteObject,
} from 'react-router';
import { RouterProvider } from 'react-router/dom';

import { type EnrollmentCurrentStep, WelcomePage } from './pages/welcome-page.js';

// Lazy-load all routes except /welcome to keep initial chunk ≤ 150 KB Brotli.
// /welcome is the entry route and is statically imported above.
const ConsentPage = lazy(() => import('./pages/consent-page.js'));
const SurveyPage = lazy(() => import('./pages/survey-page.js'));
const DonePage = lazy(() => import('./pages/done-page.js'));
const SelfAttestationPage = lazy(() => import('./pages/self-attestation-page.js'));
const EligibilityResultPage = lazy(() => import('./pages/eligibility-result-page.js'));

/**
 * WelcomeRoute — reads the `?token=` query param, calls the token-activation
 * endpoint (POST /v1/core/enrollment/invites/{token}/activate), and passes the
 * returned `currentStep` and `enrollmentSessionId` down to `WelcomePage` so
 * the ELIGIBILITY_PENDING / QUALIFYING_CONDITION_PENDING gating branches are
 * reachable at runtime.
 *
 * AC-3: Gating branches unreachable without this wiring.
 */
interface ActivationResponse {
  enrollmentSessionId: string;
  currentStep: EnrollmentCurrentStep;
  memberFirstName: string;
}

export function WelcomeRoute(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [currentStep, setCurrentStep] = useState<EnrollmentCurrentStep | undefined>(undefined);
  const [enrollmentSessionId, setEnrollmentSessionId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!token) return;

    void fetch(
      `${import.meta.env['VITE_SERVICES_BASE_URL'] as string}/v1/core/enrollment/invites/${encodeURIComponent(token)}/activate`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      },
    )
      .then((res) => {
        if (!res.ok) return;
        return res.json() as Promise<ActivationResponse>;
      })
      .then((data) => {
        if (!data) return;
        setCurrentStep(data.currentStep);
        setEnrollmentSessionId(data.enrollmentSessionId);
      })
      .catch(() => {
        // Activation failed — remain in default (normal) flow; do not block the page
      });
  }, [token]);

  return <WelcomePage currentStep={currentStep} enrollmentSessionId={enrollmentSessionId} />;
}

/**
 * RootRedirect — '/' → '/welcome', carrying the query string so invite links
 * that land on the bare origin URL (e.g. /?token=...) still activate.
 */
function RootRedirect(): JSX.Element {
  const location = useLocation();
  return <Navigate to={{ pathname: '/welcome', search: location.search }} replace />;
}

/**
 * Enroll app router — four linear routes, no auth guard, no portal shell.
 *
 * Tunnel UX: /welcome → /consent → /survey/:step → /done.
 * No header nav, no sidebar, no tab bar.
 * No RequireAuth, no PortalAuthProvider, no Cognito session required.
 */
export const routes: RouteObject[] = [
  {
    path: '/',
    element: <RootRedirect />,
  },
  {
    path: '/welcome',
    element: <WelcomeRoute />,
  },
  {
    path: '/consent',
    element: (
      <Suspense fallback={null}>
        <ConsentPage />
      </Suspense>
    ),
  },
  {
    path: '/survey',
    element: (
      <Suspense fallback={null}>
        <SurveyPage />
      </Suspense>
    ),
  },
  {
    path: '/done',
    element: (
      <Suspense fallback={null}>
        <DonePage />
      </Suspense>
    ),
  },
  {
    path: '/self-attestation',
    element: (
      <Suspense fallback={null}>
        <SelfAttestationPage />
      </Suspense>
    ),
  },
  {
    path: '/eligibility-result',
    element: (
      <Suspense fallback={null}>
        <EligibilityResultPage />
      </Suspense>
    ),
  },
];

const router = createBrowserRouter(routes);

export function App(): JSX.Element {
  return <RouterProvider router={router} />;
}
